import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '@/contexts/I18nContext';
import { SignInForm } from './SignInForm';
import { SignUpStepOne } from './SignUpStepOne';
import { SignUpStepTwo } from './SignUpStepTwo';
import { supabase } from '@/integrations/supabase/client';
import { ProfilePersonalizationWizard } from '../onboarding/ProfilePersonalizationWizard';
import { Button } from '@/components/ui/button';

interface AuthFormProps {
  isSignUp: boolean;
  setIsSignUp?: (value: boolean) => void;
  onEmailSent?: (message: string) => void;
  signUpStep?: number;
  setSignUpStep?: (step: number) => void;
  setHideOnboarding?: (hide: boolean) => void;
}

export const AuthForm = ({ isSignUp, setIsSignUp, onEmailSent, signUpStep, setSignUpStep, setHideOnboarding }: AuthFormProps) => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const { toast } = useToast();
  const { currentLanguage, t } = useI18n();
  const [currentStep, setCurrentStep] = useState(signUpStep || 1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    birthday: '',
    country: '',
    state: '',
    city: '',
    beltRank: 'white',
    academy: '',
    isInstructor: false,
    username: '',
    main_academy_id: '',
    jiu_jitsu_school: '',
  });

  // Sync with external step if provided
  // eslint-disable-next-line react-hooks/exhaustive-deps
  if (signUpStep && signUpStep !== currentStep) setCurrentStep(signUpStep);

  useEffect(() => {
    if (setHideOnboarding) {
      setHideOnboarding(false);
    }
  }, [setHideOnboarding]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleContinue = () => {
    setCurrentStep(2);
    if (setSignUpStep) setSignUpStep(2);
  };

  const handleBack = () => {
    setCurrentStep(1);
    if (setSignUpStep) setSignUpStep(1);
    if (setHideOnboarding) setHideOnboarding(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate passwords match
      if (formData.password !== formData.confirmPassword) {
        toast({
          title: t('auth.error'),
          description: t('auth.step_two.passwords_not_match'),
          variant: "destructive"
        });
        setLoading(false);
        return;
      }

      // Validate required fields
      if (!formData.email || !formData.fullName || !formData.birthday || !formData.country || !formData.beltRank || !formData.username) {
        toast({
          title: t('auth.error'),
          description: t('auth.step_one.fill_required_fields'),
          variant: "destructive"
        });
        setLoading(false);
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast({
          title: t('auth.error'),
          description: t('auth.step_one.email_invalid'),
          variant: "destructive"
        });
        setLoading(false);
        return;
      }

      // Prepare user metadata
      const userData = {
        fullName: formData.fullName,
        birthday: formData.birthday,
        country: formData.country,
        state: formData.state,
        city: formData.city,
        beltRank: formData.beltRank,
        academy: formData.academy,
        isInstructor: formData.isInstructor,
        username: formData.username,
        main_academy_id: formData.main_academy_id,
        jiu_jitsu_school: formData.jiu_jitsu_school,
        preferred_language: currentLanguage,
      };

      // Create the account
      const { error } = await signUp(formData.email, formData.password, userData);
      
      if (error) {
        if (
          error.message?.toLowerCase().includes('already registered') ||
          error.message?.toLowerCase().includes('duplicate key') ||
          error.message?.toLowerCase().includes('already exists') ||
          error.details?.toLowerCase().includes('duplicate key') ||
          error.details?.toLowerCase().includes('already exists') ||
          error.code === '23505'
        ) {
        toast({
            title: t('auth.email_already_registered'),
            description: t('auth.email_already_registered_description'),
          variant: "destructive"
        });
      } else {
        toast({
            title: t('auth.sign_up_error'),
            description: t('auth.failed_to_create_account'),
            variant: "destructive"
          });
        }
      } else {
        // Sign out to remove any active session
        await supabase.auth.signOut();

        if (onEmailSent) {
          onEmailSent(t('auth.account_created_success'));
        }

        // Reset form data
        setFormData({
          email: '',
          password: '',
          confirmPassword: '',
          fullName: '',
          birthday: '',
          country: '',
          state: '',
          city: '',
          beltRank: 'white',
          academy: '',
          isInstructor: false,
          username: '',
          main_academy_id: '',
          jiu_jitsu_school: '',
        });

        // Switch to sign in form
        if (setIsSignUp) setIsSignUp(false);

        // Redirect to login page
        navigate('/');
      }
    } catch (error) {
      toast({
        title: t('auth.error'),
        description: t('auth.unexpected_error'),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isSignUp) {
    return <SignInForm onEmailSent={onEmailSent} />;
  }

  return (
    <div className="w-full">
      {isSignUp && currentStep === 1 ? (
        <SignUpStepOne 
          formData={formData} 
          onInputChange={handleInputChange} 
          onContinue={handleContinue} 
          loading={loading} 
        />
      ) : isSignUp && currentStep === 2 ? (
        <SignUpStepTwo
          formData={formData}
          onInputChange={handleInputChange}
          onSubmit={handleSubmit}
          onBack={handleBack}
          loading={loading}
        />
      ) : null}
    </div>
  );
};
